import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { PageSection } from 'Components/Layout';
import { WorksheetImagesAndRemarksSection, WorksheetTestedBySection } from 'Components/Worksheets/Core';
import { IWorksheetComponent, WorksheetRootComponent } from 'Components/Worksheets/Core/types';
import { PortionDebugViewButton } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Components';
import ids from 'Components/Worksheets/QestIds';
import { hasNonEmptyValue, valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { not } from 'ramda';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { createSeparationCountSelector } from '../Helpers';
import {
  SpecimenPreparationPageSection,
  TestPreparationPageSection,
  WashingPageSection,
  WholePortionPreparationSection,
} from './Sections';
import { ResultsPageSection } from './Sections/Results';
import { InitialSeparationPageSection, SeparationDuringSievingPageSection } from './Sections/Separation';
import FinePortionSubspecimenDryMassesSection from './Sections/Separation/FinePortionSubspecimenDryMassesSection';
import { PortionSievingPageSection } from './Sections/Sieving';
import { TestProcedure } from './types';

const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';
const qestIds = [ids.psdSieveAnalysis.BS_EN_933_1.Rev2012];

/**
 * Group of sections required to perform separation and sieve produced portions.
 * Recursive as separations are nested.
 *
 * Name prefix should be the path of the portion being separated
 */
let SeparationSectionGroup: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, namePrefix }): ReactElement => {
  const isInitialSeparation =
    useSelector(
      pipe(createWorksheetSelector!('data.Preparation.InitialSeparationPerformed', false), valueOrDefault(false))
    ) &&
    (namePrefix?.endsWith('WholePortion') ?? false);
  const isSeparationDuringSieving = !isInitialSeparation;

  const isFinePortionSubspecimenWashed = useSelector(
    pipe(createWorksheetSelector!(`Separation.FinePortion.Subspecimen.Washing.IsWashed`), valueOrDefault(false))
  );

  const isLastSeparation = useSelector(
    pipe(
      createWorksheetSelector!('Separation.FinePortion.Subspecimen.Separation'),
      (nextSeparation) => hasNonEmptyValue(nextSeparation),
      not
    )
  );

  return (
    <>
      {isInitialSeparation && <InitialSeparationPageSection />}
      <NamePrefixModifier appendNamePrefix={'Separation.CoarsePortion'}>
        {isInitialSeparation && <WashingPageSection />}
        <PortionSievingPageSection />
      </NamePrefixModifier>
      {isSeparationDuringSieving && <SeparationDuringSievingPageSection />}

      <NamePrefixModifier appendNamePrefix={'Separation.FinePortion'}>
        {isInitialSeparation && <FinePortionSubspecimenDryMassesSection />}
        <NamePrefixModifier appendNamePrefix={'Subspecimen'}>
          {isFinePortionSubspecimenWashed && <WashingPageSection />}
          {isLastSeparation ? <PortionSievingPageSection /> : <SeparationSectionGroup />}
        </NamePrefixModifier>
      </NamePrefixModifier>
    </>
  );
};

SeparationSectionGroup = withInjectedProps(SeparationSectionGroup);

const PsdSieveAnalysisBsEnWorksheetBase: WorksheetRootComponent = ({ createWorksheetSelector }) => {
  const isWholePortionWashed = useSelector(
    pipe(createWorksheetSelector!('Preparation.MaterialWashed'), valueOrDefault(false))
  );

  const isWholePortionSeparated = useSelector(
    pipe(createSeparationCountSelector(createWorksheetSelector!), (c) => c !== 0)
  );

  const isAnnexA = useSelector(pipe(createWorksheetSelector!('Preparation.Method'), (m) => m === TestProcedure.AnnexA));

  return (
    <>
      {isDevMode && (
        <PageSection>
          <PortionDebugViewButton />
        </PageSection>
      )}

      <TestPreparationPageSection />
      <SpecimenPreparationPageSection />

      <NamePrefixModifier appendNamePrefix={`WholePortion`}>
        <WholePortionPreparationSection />
        {!isAnnexA && isWholePortionWashed && <WashingPageSection />}
        {isWholePortionSeparated ? <SeparationSectionGroup /> : <PortionSievingPageSection />}
      </NamePrefixModifier>

      <PageSection>
        <WorksheetTestedBySection />
      </PageSection>

      <ResultsPageSection />

      <PageSection heading="Images and Remarks">
        <WorksheetImagesAndRemarksSection />
      </PageSection>
    </>
  );
};

PsdSieveAnalysisBsEnWorksheetBase.qestIds = qestIds;
const PsdSieveAnalysisBsEnWorksheet = withInjectedProps(PsdSieveAnalysisBsEnWorksheetBase);

export default PsdSieveAnalysisBsEnWorksheet;
export { PsdSieveAnalysisBsEnWorksheet };
